import {
  Body,
  bold,
  Box,
  Button,
  Card,
  CheckboxField,
  Children,
  Dialog,
  LocalizedString,
  Space,
  Stack,
  useIsMobileLayout,
  useIsTouchScreen,
} from "@dbn/omnichannel-design-system-g4p";
// eslint-disable-next-line no-restricted-imports
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { useState } from "react";
import { option } from "fp-ts";
import { VirtualCardsDialogChild } from "../Common/Dialogs/VirtualCardsDialog/VirtualCardsDialogChild";
import { Option } from "fp-ts/Option";
import * as classnames from "./CustomCard.css";
import { RuntimeLocaleKey, useFormatMessage } from "../intl";

type Props = {
  tooltipButtonAriaLabel?: LocalizedString;
  tooltipCloseIconAriaLabel?: LocalizedString;
  checkBoxValue?: Option<boolean>;
  checkBoxOnChange: (data: {
    physicalCardSelected: Option<boolean>;
  }) => unknown;
  checkBoxName: string;
  checkBoxDisabled?: boolean;
  cardTitle: LocalizedString;
  cardDescription: LocalizedString;
  showModalLabel: LocalizedString;
  showModalLabelAriaLabel?: LocalizedString;
  imgUrl: string;
  altTextMessage: string;
  dialogTitle: LocalizedString;
  dialogContent: Children;
  tooltipInfoTitle?: LocalizedString;
  tooltipInfoContent?: LocalizedString;
  physicalCard: boolean;
  imageCardHorizontal: boolean;
  raiseCard: boolean;
};

export const CustomCard = (props: Props) => {
  const [showModal, setShowModal] = useState(false);
  const isBranchExperienceTouchScreen = useIsTouchScreen();
  const isMobileLayout = useIsMobileLayout();
  const formatMessage = useFormatMessage();

  const checkBoxValue =
    props.checkBoxValue && option.isSome(props.checkBoxValue)
      ? props.checkBoxValue.value
      : false;

  return (
    <Box grow shrink column>
      <Card hoverHighlighted>
        <Stack units={3} shrink grow fluid className={"custom-card-wrapper"}>
          <Box column shrink grow>
            <CheckboxField
              disabled={!!props.checkBoxDisabled}
              value={checkBoxValue}
              name={props.checkBoxName}
              onChange={(data: boolean) =>
                props.checkBoxOnChange({
                  physicalCardSelected: option.some(data),
                })
              }
              label={bold(props.cardTitle)}
              className={classnames.customCard}
              descriptionAriaLabel={props.tooltipButtonAriaLabel}
              tooltipCloseIconAriaLabel={props.tooltipCloseIconAriaLabel}
              description={
                !!props.tooltipInfoContent && !!props.tooltipInfoTitle
                  ? ((props.tooltipInfoTitle +
                      ": " +
                      props.tooltipInfoContent) as LocalizedString)
                  : undefined
              }
              issues={option.none}
            />
            <Space units={3} />
            <Box grow shrink>
              <Body
                size="medium"
                weight="regular"
                color={
                  !!props.checkBoxDisabled ? palette.neutral700 : palette.dark
                }
                align="left"
              >
                {props.cardDescription}
              </Body>
              {!isMobileLayout && <Space units={10} />}
            </Box>
            <Space units={2} />
            <Box grow shrink vAlignContent="bottom">
              <Button
                variant="text"
                size="default"
                action={() => {
                  setShowModal(true);
                }}
                ariaLabel={props.showModalLabelAriaLabel}
                label={props.showModalLabel}
              />
            </Box>
          </Box>
          <Box
            column
            aria-hidden={"true"}
            style={{
              maxWidth: isMobileLayout ? "100px" : "150px",
              justifyContent: props.imageCardHorizontal ? "center" : undefined,
            }}
            grow
            hAlignContent="right"
          >
            <img
              title=""
              src={props.imgUrl}
              style={{
                maxWidth: "100%",
                objectFit: "contain",
                boxShadow: props.raiseCard
                  ? "1px 4px 8px 0 rgba(0,0,0,0.5)"
                  : undefined,
                borderRadius: props.raiseCard ? 8 : undefined,
              }}
            />
          </Box>
          <>{!isMobileLayout && <Space units={10} />}</>
        </Stack>
      </Card>
      {showModal && (
        <>
          <Dialog
            ariaLabelMessageForCloseIcon={formatMessage(
              "Modal.closeButton" as RuntimeLocaleKey
            )}
            size="small"
            variant="left"
            title={props.dialogTitle}
            onDismiss={option.some(() => setShowModal(false))}
            actions={[]}
            children={props.dialogContent}
          />
          {isBranchExperienceTouchScreen && <VirtualCardsDialogChild />}
        </>
      )}
    </Box>
  );
};
